/**
 * Raid roster placement HUD — disposition / engagement / gambit cards.
 * Raid Leader trait cores bind from SSOT `raid_leader` (not Soft≪AA / Claim B walls).
 */
import { raidCatalog, type RaidEncounterStartMode } from "../../../data/raid-catalog";
import {
  escapeAttr,
  judgmentSellHelp,
  raidLeaderCore,
  type JudgmentTraitHelpId,
} from "../../../data/judgment-sell-help";
import { t } from "../../../data/strings";
import {
  canConfirmRoster,
  countByClass,
  type RaidRosterState,
} from "../../../features/raid/raid-roster-rules";
import { formatDisplay, formatInt } from "../../../view/display-format";
import {
  rosterCardsForPool,
  type RaidRosterCardSnap,
  type RaidRosterSellBarId,
} from "../../../features/raid/raid-roster-card";
import { getUiLayout } from "../../layout";
import { rosterCardHtml, rosterPanelHtml } from "./roster-markup";

export type RaidRosterHudHandlers = {
  onToggle: (defId: string) => void;
  onEncounterStart: (mode: RaidEncounterStartMode) => void;
  onConfirm: () => void;
  onBack: () => void;
};

const HELP_ENGAGE = ["hold", "kite", "skirmish"] as const;

const HELP_COMBAT = ["hp", "damage", "range", "speed"] as const;

function sellLabel(id: JudgmentTraitHelpId): string {
  return judgmentSellHelp().traits[id].nameKo;
}

function sellTitle(id: JudgmentTraitHelpId): string {
  const doc = judgmentSellHelp();
  return `${doc.traits[id].nameKo} — ${raidLeaderCore(doc, id)}`;
}

function axisBar(
  id: RaidRosterSellBarId,
  value: number,
  dominant: boolean,
): string {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  const demoted = id === "grit" ? " is-demoted" : "";
  const dom = dominant ? " is-dominant" : "";
  const title = escapeAttr(sellTitle(id));
  return `<div class="raid-roster-axis${dom}${demoted}" title="${title}" data-sell="${id}">
    <span class="raid-roster-axis-lab" data-axis-lab="${id}"></span>
    <span class="raid-roster-axis-track"><span class="raid-roster-axis-fill" style="width:${pct}%"></span></span>
    <span class="raid-roster-axis-val">${pct}</span>
  </div>`;
}

function combatStripHtml(card: RaidRosterCardSnap): string {
  const c = card.combat;
  return [
    `<span>${t("raid.stat.hp")} ${formatInt(c.hp)}</span>`,
    `<span>${t("raid.stat.damage")} ${formatInt(c.damage)}</span>`,
    `<span>${t("raid.stat.range")} ${formatDisplay(c.attackRange, 1)}</span>`,
    `<span>${t("raid.stat.speed")} ${formatDisplay(c.speed, 1)}</span>`,
  ].join("");
}

function helpBodyHtml(): string {
  const doc = judgmentSellHelp();
  const sellIds: JudgmentTraitHelpId[] = ["instinct", "insight", "bond", "grit"];
  const traitRows = sellIds
    .map((id) => {
      const tr = doc.traits[id];
      const demoted = id === "grit" ? " is-demoted" : "";
      return `
    <div class="raid-roster-help-row${demoted}">
      <strong class="raid-roster-help-lab">${escapeAttr(tr.nameKo)}</strong>
      <p class="raid-roster-help-blurb">${escapeAttr(raidLeaderCore(doc, id))}</p>
    </div>`;
    })
    .join("");
  const combatRows = HELP_COMBAT.map(
    (s) => `
    <div class="raid-roster-help-row">
      <strong class="raid-roster-help-lab">${t(`raid.stat.${s}`)}</strong>
      <span class="raid-roster-help-hi">${t(`raid.help.stat.${s}.high`)}</span>
      <span class="raid-roster-help-lo">${t(`raid.help.stat.${s}.low`)}</span>
    </div>`,
  ).join("");
  const engageRows = HELP_ENGAGE.map(
    (e) => `
    <div class="raid-roster-help-row">
      <strong class="raid-roster-help-lab">${t(`raid.engage.${e}`)}</strong>
      <span class="raid-roster-help-hi">${t(`raid.help.engage.${e}`)}</span>
    </div>`,
  ).join("");
  return `
    <p class="raid-roster-help-intro">${escapeAttr(doc.raidLeader.introPlain)}</p>
    <p class="raid-roster-help-sec">${t("raid.help.sec.axes")}</p>
    ${traitRows}
    <p class="raid-roster-help-sec">${t("raid.help.sec.combat")}</p>
    ${combatRows}
    <p class="raid-roster-help-sec">${t("raid.help.sec.engage")}</p>
    ${engageRows}
  `;
}

function fillCard(btn: HTMLButtonElement, card: RaidRosterCardSnap, selected: boolean): void {
  btn.dataset.defId = card.defId;
  btn.classList.toggle("is-selected", selected);
  btn.setAttribute("aria-pressed", selected ? "true" : "false");

  const name = btn.querySelector("[data-name]");
  if (name) name.textContent = t(card.nameKey);
  const cls = btn.querySelector("[data-class]");
  if (cls) cls.textContent = t(card.classKey);
  const quirk = btn.querySelector("[data-quirk]");
  if (quirk) quirk.textContent = t(card.quirkKey);
  const eng = btn.querySelector("[data-engage]");
  if (eng) eng.textContent = t(card.engagementKey);
  const dom = btn.querySelector("[data-dominant]");
  if (dom) {
    dom.textContent = t("raid.roster.dominant", {
      axis: sellLabel(card.dominantSell),
    });
  }
  const exp = btn.querySelector("[data-experimental]") as HTMLElement | null;
  if (exp) {
    exp.hidden = !card.experimental;
    exp.textContent = card.experimental ? t("raid.roster.experimental") : "";
  }
  btn.classList.toggle("is-experimental", card.experimental);

  const combat = btn.querySelector("[data-combat]");
  if (combat) combat.innerHTML = combatStripHtml(card);

  const axes = btn.querySelector("[data-axes]");
  if (axes) {
    axes.innerHTML = card.sellBars
      .map((id) =>
        axisBar(id, card.judgment[id], id === card.dominantSell),
      )
      .join("");
    for (const lab of axes.querySelectorAll("[data-axis-lab]")) {
      const id = (lab as HTMLElement).dataset.axisLab as JudgmentTraitHelpId;
      lab.textContent = sellLabel(id);
    }
  }

  const gambits = btn.querySelector("[data-gambits]");
  if (gambits) {
    gambits.innerHTML = card.gambits
      .map(
        (g) =>
          `<span class="raid-roster-gambit">${t(g.condKey)}→${t(g.behaviorKey)}</span>`,
      )
      .join("");
  }

  const traits = btn.querySelector("[data-traits]");
  if (traits) {
    const keys = card.traitKeys.slice(0, 3);
    traits.innerHTML = keys
      .map((k) => `<span class="raid-roster-trait">${t(k)}</span>`)
      .join("");
  }
}

export function mountRaidRosterHud(handlers: RaidRosterHudHandlers): {
  sync: (state: RaidRosterState) => void;
  destroy: () => void;
} {
  const ui = getUiLayout();
  ui.sync();
  ui.clearShell();
  const frame = ui.frame;
  frame.style.display = "flex";
  frame.style.flexDirection = "column";
  frame.style.alignItems = "center";
  frame.style.justifyContent = "flex-start";
  frame.style.pointerEvents = "auto";
  // Clip to SafeFrame; panel scrolls inside (mobile short viewports).
  frame.style.overflow = "hidden";
  frame.style.minHeight = "0";
  ui.overlay.style.pointerEvents = "auto";

  const spacer = document.createElement("div");
  spacer.className = "raid-roster-spacer";
  spacer.setAttribute("aria-hidden", "true");
  frame.appendChild(spacer);

  const wrap = document.createElement("div");
  wrap.innerHTML = rosterPanelHtml();
  const panel = wrap.firstElementChild as HTMLElement;
  frame.appendChild(panel);

  const hint = panel.querySelector("[data-hint]");
  if (hint) hint.textContent = t("raid.roster.hint");
  const helpSum = panel.querySelector("[data-help-sum]");
  if (helpSum) {
    helpSum.textContent = t("raid.help.title", {
      title: judgmentSellHelp().title,
    });
  }
  const helpBody = panel.querySelector("[data-help-body]");
  if (helpBody) helpBody.innerHTML = helpBodyHtml();
  const confirmBtn = panel.querySelector(
    "[data-confirm]",
  ) as HTMLButtonElement;
  const backBtn = panel.querySelector("[data-back]") as HTMLButtonElement;
  confirmBtn.textContent = t("raid.roster.confirm");
  backBtn.textContent = t("raid.roster.back");

  const startLab = panel.querySelector("[data-start-lab]");
  if (startLab) startLab.textContent = t("raid.roster.start.label");
  const assaultBtn = panel.querySelector(
    "[data-start-assault]",
  ) as HTMLButtonElement;
  const bossBtn = panel.querySelector(
    "[data-start-boss]",
  ) as HTMLButtonElement;
  assaultBtn.textContent = t("raid.roster.start.assault");
  bossBtn.textContent = t("raid.roster.start.boss");
  assaultBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onEncounterStart("assault");
  });
  bossBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onEncounterStart("boss");
  });

  const pool = panel.querySelector("[data-pool]") as HTMLElement;
  const cards = rosterCardsForPool();
  const tpl = document.createElement("template");
  tpl.innerHTML = rosterCardHtml().trim();
  for (const card of cards) {
    const btn = tpl.content.firstElementChild!.cloneNode(
      true,
    ) as HTMLButtonElement;
    fillCard(btn, card, false);
    btn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      handlers.onToggle(card.defId);
    });
    pool.appendChild(btn);
  }

  confirmBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onConfirm();
  });
  backBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onBack();
  });

  return {
    sync(state: RaidRosterState) {
      ui.sync();
      for (const btn of pool.querySelectorAll<HTMLButtonElement>("[data-card]")) {
        const id = btn.dataset.defId;
        if (!id) continue;
        const card = cards.find((c) => c.defId === id);
        if (!card) continue;
        fillCard(btn, card, state.selected.includes(id));
      }
      const count = panel.querySelector("[data-count]");
      if (count) {
        count.textContent = t("raid.roster.count", {
          n: state.selected.length,
          cap: raidCatalog.balance.partyCap,
          min: raidCatalog.balance.partyMin,
        });
      }
      const classes = panel.querySelector("[data-classes]");
      if (classes) {
        const c = countByClass(state);
        classes.textContent = `${t("raid.class.tank")} ${c.tank} · ${t("raid.class.dps")} ${c.dps} · ${t("raid.class.healer")} ${c.healer}`;
      }
      const msg = panel.querySelector("[data-msg]");
      if (msg) {
        msg.textContent = state.messageKey ? t(state.messageKey) : "";
      }
      assaultBtn.classList.toggle(
        "is-selected",
        state.encounterStart === "assault",
      );
      bossBtn.classList.toggle("is-selected", state.encounterStart === "boss");
      assaultBtn.setAttribute(
        "aria-pressed",
        state.encounterStart === "assault" ? "true" : "false",
      );
      bossBtn.setAttribute(
        "aria-pressed",
        state.encounterStart === "boss" ? "true" : "false",
      );
      confirmBtn.disabled = !canConfirmRoster(state);
    },
    destroy() {
      ui.clearShell();
    },
  };
}
